import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import DeleteAccountModal from '../Auth/DeleteAccountModal';
import DevicesSection from './DevicesSection';
import { userInitials, userDisplayName } from '../../utils/userDisplay';
import './AccountTab.scss';

/**
 * AccountTab
 *
 * A dedicated account screen (reached from the avatar menu → "Account"). Holds
 * the profile card and a bottom "Danger zone" with account deletion — the
 * standard pattern (Settings/Account → bottom), so deletion is findable per
 * App Store 5.1.1(v) but not sitting in the primary menu next to Log Out.
 *
 * The Danger zone (account deletion) shows on every platform — required by
 * iOS/Play and offered on web + desktop too for parity and GDPR/CCPA erasure.
 */

const PLAN_COLORS = { free: '#888', pro: '#00bcd4', goat: '#FFD700' };

function AccountTab() {
    const user = useSelector((s) => s.auth.user);
    const [showDelete, setShowDelete] = useState(false);
    const [appVersion, setAppVersion] = useState('');
    useEffect(() => {
        const getVersion = window.electron?.version?.get;
        if (!getVersion) return;
        Promise.resolve(getVersion()).then(v => v && setAppVersion(v)).catch(() => {});
    }, []);

    if (!user) return null;

    const initials = userInitials(user);
    const displayName = userDisplayName(user);
    const plan = (user.subscription_type || 'free').toLowerCase();
    const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1);
    const planColor = PLAN_COLORS[plan] || PLAN_COLORS.free;

    return (
        <div className="account-tab" style={{ padding: '20px 16px', maxWidth: 560, margin: '0 auto', color: 'var(--color-text-primary)' }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 22, fontWeight: 700 }}>Account</h2>

            <div className="account-profile-card" style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: 16,
                borderRadius: 14,
            }}>
                <div style={{
                    width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--color-accent)', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, fontWeight: 700,
                }}>{initials}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                    <span style={{ fontWeight: 600 }}>{displayName}</span>
                    <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</span>
                    <span style={{
                        alignSelf: 'flex-start', marginTop: 2, fontSize: 12, fontWeight: 700,
                        color: planColor, border: `1px solid ${planColor}`, borderRadius: 8, padding: '1px 8px',
                    }}>{planLabel}</span>
                </div>
            </div>

            <DevicesSection />

            <div style={{ marginTop: 28 }}>
                <div style={{
                    fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                    color: 'var(--color-text-tertiary)', marginBottom: 8,
                }}>Danger zone</div>
                <div style={{ border: '1px solid var(--color-border)', borderRadius: 14, overflow: 'hidden' }}>
                    <button
                        type="button"
                        onClick={() => setShowDelete(true)}
                        style={{
                            width: '100%', boxSizing: 'border-box',
                            display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
                            minHeight: 50, padding: '0 16px', lineHeight: 1,
                            background: 'transparent', border: 'none',
                            color: 'var(--color-danger)', fontSize: 15, fontWeight: 600, cursor: 'pointer',
                        }}
                    >
                        Delete account
                    </button>
                </div>
                <p style={{ marginTop: 8, fontSize: 12, color: 'var(--color-text-tertiary)', lineHeight: 1.5 }}>
                    Permanently deletes your account and all associated data. This can't be undone.
                </p>
            </div>

            {appVersion && (
                <p style={{ marginTop: 24, textAlign: 'center', fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                    MixGoat v{appVersion}
                </p>
            )}

            {showDelete && <DeleteAccountModal onClose={() => setShowDelete(false)} />}
        </div>
    );
}

export default AccountTab;
